// Copyright (c) Docugami, Inc. All rights reserved.

import { CoachMarkId, OrganizationCoachMarkLookup } from "coachmarks/CoachMarkTypes";
import { StorageAccessor, StorageKey } from "utils/StorageAccessor";

export interface ICoachMarkTracker {
    getCoachMark(id: CoachMarkId): boolean;
    setCoachMark(id: CoachMarkId): void;
    clearCoachMark(id: CoachMarkId): void;
    resetCoachMarks(): void;
}

export class CoachMarkTracker implements ICoachMarkTracker {
    private readonly _orgId: string;
    private readonly _accessor = new StorageAccessor();
    private _orgLookup: OrganizationCoachMarkLookup | null = null;

    constructor(orgId: string) {
        this._orgId = orgId;
    }

    private get lookup(): {[coachMarkId: string]: boolean} {
        return this.orgLookup[this._orgId];
    }

    private get orgLookup(): OrganizationCoachMarkLookup {
        if (!this._orgLookup) {
            this._orgLookup = this._accessor.getObject<OrganizationCoachMarkLookup>(StorageKey.CoachMarkLookup, {})!;

            if (!this._orgLookup[this._orgId]) {
                this._orgLookup[this._orgId] = {};
                this._accessor.setObject(StorageKey.CoachMarkLookup, this._orgLookup);
            }
        }

        return this._orgLookup;
    }

    getCoachMark(id: CoachMarkId): boolean {
        return !!this.lookup[id];
    }

    setCoachMark(id: CoachMarkId): void {
        this.lookup[id] = true;
        this.saveState();
    }

    clearCoachMark(id: CoachMarkId): void {
        if (!!this.lookup[id]) {
            delete this.lookup[id];
            this.saveState();
        }
    }

    resetCoachMarks(): void {
        this.orgLookup[this._orgId] = {};
        this.saveState();
    }

    private saveState() {
        if (!!this._orgLookup) {
            this._accessor.setObject(StorageKey.CoachMarkLookup, this.orgLookup);
        }
    }
}
